import { useEffect, useRef, useState, type ReactNode, type Ref } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Loader2, X, ZoomIn, ZoomOut } from "lucide-react";
import { api, type WeaponAlert } from "../api";
import { ClipScrubber } from "./ClipScrubber";
import { ClipPlayerCanvas, useDirectReplay } from "../useDirectReplay";
import { useBackdropDismiss } from "./Modal";
import { useScope } from "./ScopeContext";
import { useWords } from "../words";

// One type scale for every alert surface (page cards, ribbon, dialog). Alarm
// cards used to mix four font sizes and three background treatments, which
// made a wall of them read as noise rather than as a list.
export const SECTION_TITLE = "text-sm font-semibold text-white";
export const META_TEXT = "text-xs text-bf-muted";
/** Secondary text on the ribbon's washed background, where bf-muted is too
 *  dim to read. */
export const RIBBON_META = "text-xs text-white/85";
export const ACTION_TEXT =
  "text-sm font-medium text-bf-magenta/90 transition hover:text-bf-magenta";
export const ALERT_GRID = "grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4";
export const ALERTS_INITIAL_VISIBLE = 8;
export const ALERT_THUMB_IMG = "max-h-full max-w-full object-contain object-center";
// Neutral surface tint, not black: a black box behind a letterboxed crop reads
// as a broken image, and looked nothing like the rest of the app.
export const ALERT_THUMB_BOX =
  "aspect-video w-full overflow-hidden bg-white/[0.04] flex items-center justify-center";

export function useAlertImageBlob(
  alertId: string,
  kind: "crop" | "frame" = "crop",
  enabled = true,
) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setFailed(false);
      setSrc(null);
      return;
    }
    let objectUrl: string | null = null;
    let cancelled = false;
    setFailed(false);
    setSrc(null);

    fetch(`/api/alerts/weapon/${encodeURIComponent(alertId)}/${kind}`, {
      credentials: "include",
    })
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [alertId, enabled, kind]);

  return { src, failed };
}

export function useAlertCropBlob(alertId: string, enabled = true) {
  return useAlertImageBlob(alertId, "crop", enabled);
}

export function AlertCropImage({
  alertId,
  alt,
  className,
  containerClassName,
  buttonClassName,
  onClick,
  matchHeight,
}: {
  alertId: string;
  alt: string;
  /** Applied to the <img> (defaults to cover-fill). */
  className?: string;
  /** Wrapper around the image (defaults to aspect-video tile). */
  containerClassName?: string;
  /** Wrapper button when onClick is set (defaults to full-width tile). */
  buttonClassName?: string;
  onClick?: () => void;
  /** Stretch to the sibling text height; width follows the image aspect ratio. */
  matchHeight?: boolean;
}) {
  const hostRef = useRef<HTMLButtonElement | HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);
  const { src, failed } = useAlertCropBlob(alertId, inView);
  const [ratio, setRatio] = useState<number | null>(null);

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { rootMargin: "160px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const onImgMeta = (img: HTMLImageElement) => {
    if (img.naturalHeight > 0) setRatio(img.naturalWidth / img.naturalHeight);
  };

  // Flex stretch sets height; the image's intrinsic width otherwise wins and
  // the box goes tall-and-skinny. Size width from the stretched height.
  useEffect(() => {
    const el = hostRef.current;
    if (!matchHeight || !el || !ratio) return;
    const apply = () => {
      const h = el.clientHeight;
      el.style.width = h > 0 ? `${h * ratio}px` : "";
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => {
      ro.disconnect();
      el.style.width = "";
    };
  }, [matchHeight, ratio]);

  const boxClass = containerClassName ?? ALERT_THUMB_BOX;
  const imgClass = className ?? ALERT_THUMB_IMG;

  let inner: ReactNode;
  if (failed) {
    inner = (
      <div className="grid h-full min-h-[4rem] w-full place-items-center px-2 text-center text-xs text-bf-muted">
        Image unavailable
      </div>
    );
  } else if (!src) {
    inner = (
      <div className="h-full min-h-[4rem] w-full animate-pulse bg-white/[0.06]" aria-hidden />
    );
  } else {
    inner = (
      <img
        src={src}
        alt={alt}
        className={imgClass}
        draggable={false}
        onLoad={(e) => onImgMeta(e.currentTarget)}
      />
    );
  }

  const body = <div className={boxClass}>{inner}</div>;

  if (matchHeight) {
    return (
      <button
        ref={hostRef as Ref<HTMLButtonElement>}
        type="button"
        onClick={onClick}
        aria-label="Open crop viewer"
        className={[
          "relative",
          buttonClassName ??
            "shrink-0 self-stretch overflow-hidden rounded-md focus:outline-none focus:ring-2 focus:ring-bf-violet/60",
        ].join(" ")}
        style={ratio ? undefined : { width: "5.5rem" }}
      >
        <div className="absolute inset-0 overflow-hidden bg-white/[0.04]">
          {failed ? (
            <div className="grid h-full w-full place-items-center px-2 text-center text-xs text-bf-muted">
              Image unavailable
            </div>
          ) : !src ? (
            <div className="h-full w-full animate-pulse bg-white/[0.06]" aria-hidden />
          ) : (
            <img
              src={src}
              alt={alt}
              draggable={false}
              className="h-full w-full object-contain object-center"
              onLoad={(e) => onImgMeta(e.currentTarget)}
            />
          )}
        </div>
      </button>
    );
  }

  if (onClick) {
    return (
      <button
        ref={hostRef as Ref<HTMLButtonElement>}
        type="button"
        onClick={onClick}
        className={
          buttonClassName ??
          "block w-full overflow-hidden rounded-md focus:outline-none focus:ring-2 focus:ring-bf-violet/60"
        }
        aria-label="Open crop viewer"
      >
        {body}
      </button>
    );
  }

  return (
    <div ref={hostRef as Ref<HTMLDivElement>} className={boxClass}>
      {inner}
    </div>
  );
}

export function isPolicyAlert(alert: WeaponAlert): boolean {
  return alert.kind === "policy";
}

export function policyName(alert: WeaponAlert): string {
  return (alert.track_label || "").trim() || "Policy";
}

export function policyMessage(alert: WeaponAlert): string {
  return (alert.alert_text || "").trim();
}

export type AlertBodyCopy = {
  title: string;
  store: string;
  camera: string;
  message: string;
};

export function policyAlertCopy(
  alert: WeaponAlert,
  storeName: string,
  cameraName: string,
): AlertBodyCopy {
  return {
    title: policyName(alert),
    store: storeName.trim(),
    camera: cameraName.trim(),
    message: policyMessage(alert),
  };
}

/** Status pill includes the name: "Alert - Oven Turned On". */
export function alertPillText(label: string, title: string): string {
  const name = title.trim();
  return name ? `${label} - ${name}` : label;
}

export function AlertSummary({
  label,
  title,
  message,
  store,
  camera,
  date,
  time,
  pillClass,
  icon,
  afterMessage,
}: AlertBodyCopy & {
  label: string;
  date?: string;
  time: string;
  pillClass: string;
  icon: ReactNode;
  afterMessage?: ReactNode;
}) {
  const w = useWords();
  return (
    <div className="min-w-0">
      <span
        className={`inline-flex max-w-full items-center gap-1.5 rounded-full px-3 py-1 text-base font-bold ${pillClass}`}
      >
        <span className="shrink-0">{icon}</span>
        <span className="truncate">{alertPillText(label, title)}</span>
      </span>
      {message || afterMessage ? (
        <div className="mt-1 flex min-w-0 items-baseline gap-x-2.5">
          {message ? (
            <span className="min-w-0 truncate text-base text-white">{message}</span>
          ) : null}
          {afterMessage}
        </div>
      ) : null}
      {(store || camera || date || time) && (
        <div className="mt-1.5 inline-flex max-w-full flex-wrap items-center gap-x-3 gap-y-0.5 rounded-lg bg-brand-purple/10 px-2.5 py-1 text-sm text-white/80 ring-1 ring-inset ring-brand-violet/25">
          {store ? (
            <span className="truncate">
              <span className="text-white/50">{w.Store}:</span> {store}
            </span>
          ) : null}
          {camera ? (
            <span className="truncate">
              <span className="text-white/50">Camera:</span> {camera}
            </span>
          ) : null}
          {date ? (
            <span className="tabular-nums">
              <span className="text-white/50">Date:</span> {date}
            </span>
          ) : null}
          {time ? (
            <span className="tabular-nums">
              <span className="text-white/50">Time:</span> {time}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}

export function reviewCopy(alert: WeaponAlert): { headline: string; subline: string; positive: boolean } {
  if (alert.status === "confirmed") {
    if (isPolicyAlert(alert)) {
      return {
        headline: policyName(alert),
        subline: policyMessage(alert),
        positive: true,
      };
    }
    return {
      headline: "Threat detected",
      subline: "Immediate action required",
      positive: true,
    };
  }
  return {
    headline: "False alarm",
    subline: "Human review encouraged",
    positive: false,
  };
}

export function pendingCopy(alert?: WeaponAlert) {
  if (alert && isPolicyAlert(alert)) {
    return { headline: policyName(alert), subline: policyMessage(alert) };
  }
  return { headline: "Suspicious activity", subline: "Under verification" };
}

export function ribbonCopy(alert: WeaponAlert) {
  if (alert.status === "pending") return pendingCopy(alert);
  return reviewCopy(alert);
}

/**
 * Ribbon / card copy. Policy alerts use the policy name and operator
 * message; weapon alerts use the built-in lines. Store and camera are
 * always separate labeled fields.
 */
export function ribbonPremiumCopy(
  alert: WeaponAlert,
  cameraName: string,
  storeName = "",
): { label: string } & AlertBodyCopy {
  const camera = cameraName.trim();
  const store = storeName.trim();
  if (isPolicyAlert(alert)) {
    const body = policyAlertCopy(alert, store, camera);
    if (alert.status === "pending") return { label: "Verifying", ...body };
    if (alert.status === "confirmed") return { label: "Alert", ...body };
    return { label: "Cleared", ...body };
  }
  if (alert.status === "pending") {
    return {
      label: "Verifying",
      title: "Possible Weapon",
      store,
      camera,
      message: "Verification in progress",
    };
  }
  if (alert.status === "confirmed") {
    return {
      label: "Alert",
      title: "Weapon Detected",
      store,
      camera,
      message: "Immediate action required",
    };
  }
  return {
    label: "Cleared",
    title: "False Alarm",
    store,
    camera,
    message: "Reviewed — no action needed",
  };
}

/**
 * Severity styling for alert surfaces.
 *
 * Two consumers with deliberately different needs:
 *
 *  - The RIBBON is an interruption. It uses `shell`/`ring`/`chip`/`iconBright`
 *    for the full-width washed banner that makes a live alarm impossible to
 *    scroll past.
 *  - The alarm CARDS are a list you read. They use only `bar`, `pill` and
 *    `label`, on the same opaque surface for every severity, so a wall of them
 *    reads as rows rather than as competing colour blocks.
 *
 * Confirmed alarms use the marketing-site pink (#EC4899, --brand-pink).
 */
export type AlertStyle = {
  /** Card/banner surface wash (ribbon only). */
  shell: string;
  /** Border (ribbon only). */
  ring: string;
  /** Thumbnail inset ring. */
  thumbBorder: string;
  /** Icon colour. */
  icon: string;
  /** Brighter icon colour for the ribbon pill. */
  iconBright: string;
  /** Rounded icon chip background (ribbon). */
  chip: string;
  /** Badge (pill) classes. */
  pill: string;
  /** Short status word shown in the badge. */
  label: string;
  /** Headline / subline colours (ribbon). */
  headline: string;
  subline: string;
  /** Emphasis colour for the ribbon's imperative line. */
  accent: string;
  /** Left severity bar. */
  bar: string;
  /** Pulsing status dot (ribbon). */
  dot: string;
};

export function alertStatusStyle(variant: "pending" | "review", alert: WeaponAlert): AlertStyle {
  const pending = variant === "pending";
  const { positive } = pending ? { positive: false } : reviewCopy(alert);

  if (pending) {
    return {
      shell: "bg-gradient-to-r from-amber-400/30 via-amber-400/12 to-transparent",
      ring: "border-amber-400/25",
      thumbBorder: "ring-1 ring-inset ring-amber-400/40",
      icon: "text-amber-300",
      iconBright: "text-amber-200",
      chip: "bg-amber-400/15",
      pill: "bg-amber-400/10 text-amber-200 ring-1 ring-inset ring-amber-400/30",
      label: "Verifying",
      headline: "text-white",
      subline: "text-white/70",
      accent: "text-amber-200",
      bar: "bg-amber-400",
      dot: "bg-amber-300",
    };
  }
  if (positive) {
    return {
      shell: "bg-gradient-to-r from-brand-violet/50 via-brand-purple/22 to-transparent",
      ring: "border-brand-violet/35",
      thumbBorder: "ring-1 ring-inset ring-brand-pink/50",
      icon: "text-brand-pink",
      iconBright: "text-pink-300",
      chip: "bg-brand-pink/15",
      pill: "bg-brand-pink/10 text-pink-200 ring-1 ring-inset ring-brand-pink/40",
      label: "Threat",
      headline: "text-white",
      subline: "text-white/70",
      accent: "text-pink-200",
      bar: "bg-brand-pink",
      dot: "bg-brand-pink",
    };
  }
  return {
    shell: "bg-gradient-to-r from-brand-purple/30 via-brand-purple/12 to-transparent",
    ring: "border-brand-purple/30",
    thumbBorder: "ring-1 ring-inset ring-brand-violet/30",
    icon: "text-violet-300",
    iconBright: "text-violet-200",
    chip: "bg-brand-purple/15",
    pill: "bg-brand-purple/15 text-violet-200 ring-1 ring-inset ring-brand-violet/35",
    label: "False alarm",
    headline: "text-white",
    subline: "text-white/70",
    accent: "text-violet-200",
    bar: "bg-brand-violet/70",
    dot: "bg-violet-300",
  };
}

/** Shared alert card: ink-soft panel, brand hairline, severity on the left bar. */
export const ALERT_CARD =
  "relative overflow-hidden rounded-2xl border border-white/10 bg-ink-soft " +
  "shadow-lg shadow-brand-purple/10 transition hover:border-brand-violet/40";

export const ALERT_CARD_HAIRLINE =
  "absolute inset-x-0 top-0 h-px bg-gradient-to-r from-brand-purple/30 via-brand-violet/20 to-brand-fuchsia/25";

export function AlertTile({
  alert,
  variant,
  cameraLabel,
  storeLabel,
  onDismiss,
  onEnlarge,
}: {
  alert: WeaponAlert;
  variant: "pending" | "review";
  /** Human camera name; falls back to the id when unknown. */
  cameraLabel?: string;
  storeLabel?: string;
  onDismiss: (id: string, fromHistory: boolean) => void;
  onEnlarge: (alert: WeaponAlert) => void;
}) {
  const review = variant === "review";
  const copy = ribbonPremiumCopy(
    alert,
    cameraLabel || alert.camera_id,
    storeLabel || "",
  );
  const style = alertStatusStyle(variant, alert);

  return (
    <article className={`group ${ALERT_CARD}`}>
      <span className={ALERT_CARD_HAIRLINE} aria-hidden />
      <span className={`absolute inset-y-0 left-0 w-1 ${style.bar}`} aria-hidden />

      <button
        type="button"
        onClick={() => onDismiss(alert.alert_id, review)}
        className="absolute right-2 top-2 z-10 rounded-md bg-ink-soft/90 p-1 text-bf-muted opacity-0 transition hover:bg-brand-purple/20 hover:text-white focus:opacity-100 group-hover:opacity-100"
        aria-label="Dismiss alert"
      >
        <X size={14} />
      </button>

      <button
        type="button"
        onClick={() => onEnlarge(alert)}
        className="block w-full pl-1 text-left"
      >
        <div className={`overflow-hidden ${style.thumbBorder}`}>
          <AlertCropImage
            alertId={alert.alert_id}
            alt={`Alert ${alert.alert_id}`}
            containerClassName="aspect-video w-full overflow-hidden bg-white/[0.04]"
            className="h-full w-full object-cover object-center"
          />
        </div>

        <div className="p-3">
          <AlertSummary
            label={copy.label}
            title={copy.title}
            message={copy.message}
            store={copy.store}
            camera={copy.camera}
            time={new Date(alert.updated_at_ms).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
            pillClass={style.pill}
            icon={<AlertTriangle size={20} className="shrink-0" />}
          />
        </div>
      </button>
    </article>
  );
}

export function AlertImageDialog({ alert, onClose }: { alert: WeaponAlert; onClose: () => void }) {
  const crop = useAlertImageBlob(alert.alert_id, "crop");
  const [showFrame, setShowFrame] = useState(false);
  const frame = useAlertImageBlob(alert.alert_id, "frame", showFrame);
  const viewingFrame = showFrame;
  const { src, failed } = viewingFrame ? frame : crop;
  const { stores } = useScope();
  const cameras = useQuery({
    queryKey: ["cameras", "all"],
    queryFn: () => api.cameras.list(),
  });
  const cameraName =
    cameras.data?.find((c) => c.id === alert.camera_id)?.name?.trim() ||
    alert.camera_id;
  const storeKey =
    alert.store ||
    cameras.data?.find((c) => c.id === alert.camera_id)?.store ||
    "";
  const storeName =
    stores.find((s) => s.name === storeKey)?.display_name || storeKey;
  const copy = ribbonPremiumCopy(alert, cameraName, storeName);
  const replay = useDirectReplay(alert.camera_id);
  const momentMs = Math.round(alert.acquired_at_ms || alert.created_at_ms);

  useEffect(() => {
    setShowFrame(false);
  }, [alert.alert_id]);

  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);

  useEffect(() => {
    setScale(1);
    setPan({ x: 0, y: 0 });
  }, [viewingFrame, replay.playing]);

  const clampScale = (v: number) => Math.min(5, Math.max(1, v));

  function zoomBy(delta: number) {
    setScale((s) => {
      const next = clampScale(s + delta);
      if (next === 1) setPan({ x: 0, y: 0 });
      return next;
    });
  }

  function onWheel(e: React.WheelEvent) {
    e.preventDefault();
    zoomBy(e.deltaY < 0 ? 0.2 : -0.2);
  }

  function onPointerDown(e: React.PointerEvent) {
    if (scale <= 1) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!dragRef.current) return;
    setPan({
      x: dragRef.current.panX + (e.clientX - dragRef.current.x),
      y: dragRef.current.panY + (e.clientY - dragRef.current.y),
    });
  }

  function onPointerUp() {
    dragRef.current = null;
  }

  const dismiss = useBackdropDismiss(onClose);

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-50 grid place-items-center bg-black/85 backdrop-blur-sm p-4"
      {...dismiss}
    >
      <div
        className="bf-gradient-border h-[95vh] w-[96vw]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bf-gradient-border-inner flex h-full flex-col overflow-hidden p-4">
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <AlertSummary
                label={copy.label}
                title={copy.title}
                message={copy.message}
                store={copy.store}
                camera={copy.camera}
                date={new Date(alert.updated_at_ms).toLocaleDateString([], {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
                time={new Date(alert.updated_at_ms).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
                pillClass={
                  alert.status === "pending"
                    ? alertStatusStyle("pending", alert).pill
                    : alertStatusStyle("review", alert).pill
                }
                icon={<AlertTriangle size={20} className="shrink-0" />}
              />
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-2">
              {!replay.playing && (
                <>
                  <div
                    role="group"
                    aria-label="Image view"
                    className="mr-1 flex items-center gap-1 rounded-lg border border-white/10 bg-bf-surface/60 p-1"
                  >
                    <button
                      type="button"
                      aria-pressed={!viewingFrame}
                      onClick={() => setShowFrame(false)}
                      className={
                        "rounded-md px-2.5 py-1 text-xs font-medium transition " +
                        (!viewingFrame
                          ? "bg-bf-violet/30 text-white"
                          : "text-bf-muted hover:bg-white/5 hover:text-bf-text")
                      }
                    >
                      Crop
                    </button>
                    <button
                      type="button"
                      aria-pressed={viewingFrame}
                      onClick={() => setShowFrame(true)}
                      className={
                        "rounded-md px-2.5 py-1 text-xs font-medium transition " +
                        (viewingFrame
                          ? "bg-bf-violet/30 text-white"
                          : "text-bf-muted hover:bg-white/5 hover:text-bf-text")
                      }
                    >
                      Full frame
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => zoomBy(-0.25)}
                    className="rounded p-2 text-bf-muted hover:bg-white/10 hover:text-bf-text"
                    aria-label="Zoom out"
                  >
                    <ZoomOut size={18} />
                  </button>
                  <span className="min-w-[3rem] text-center text-xs text-bf-muted">
                    {Math.round(scale * 100)}%
                  </span>
                  <button
                    type="button"
                    onClick={() => zoomBy(0.25)}
                    className="rounded p-2 text-bf-muted hover:bg-white/10 hover:text-bf-text"
                    aria-label="Zoom in"
                  >
                    <ZoomIn size={18} />
                  </button>
                </>
              )}
              {replay.playing && (
                <button
                  type="button"
                  onClick={() => replay.stop()}
                  className="rounded-lg border border-white/15 bg-white/10 px-3 py-1.5 text-sm font-medium text-white hover:bg-white/15"
                >
                  Back to image
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="rounded p-2 text-bf-muted hover:bg-white/10 hover:text-bf-text"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          <div
            className="relative min-h-0 flex-1 overflow-hidden rounded-md bg-white/[0.03]"
            onWheel={replay.playing ? undefined : onWheel}
            onPointerDown={replay.playing ? undefined : onPointerDown}
            onPointerMove={replay.playing ? undefined : onPointerMove}
            onPointerUp={replay.playing ? undefined : onPointerUp}
            onPointerCancel={replay.playing ? undefined : onPointerUp}
            onDoubleClick={
              replay.playing ? undefined : () => zoomBy(scale < 2 ? 1 : -1)
            }
          >
            <ClipPlayerCanvas
              canvasRef={replay.canvasRef}
              visible={replay.playing && !replay.pending}
              cameraId={alert.camera_id}
            />
            {replay.playing ? (
              replay.pending ? (
                <div className="absolute inset-0 grid place-items-center">
                  <Loader2 size={28} className="animate-spin text-white/80" />
                </div>
              ) : null
            ) : failed ? (
              <div className="grid h-full place-items-center text-sm text-bf-muted">
                {viewingFrame ? "Frame unavailable" : "Crop unavailable"}
              </div>
            ) : !src ? (
              <div className="h-full animate-pulse bg-white/[0.06]" />
            ) : (
              <img
                src={src}
                alt={copy.title || `Alert ${alert.alert_id}`}
                draggable={false}
                className="h-full w-full select-none object-contain object-center transition-transform duration-100"
                style={{
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
                  cursor: scale > 1 ? "grab" : "zoom-in",
                }}
              />
            )}
          </div>
          <div className="mt-3 shrink-0">
            <ClipScrubber
              cameraId={alert.camera_id}
              cameraName={cameraName}
              startMs={momentMs}
              endMs={momentMs}
              snapshotUrl={`/api/alerts/weapon/${encodeURIComponent(alert.alert_id)}/${
                viewingFrame ? "frame" : "crop"
              }`}
              replay={replay}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
